<div class="space-y-8">
  <!-- Account Summary Cards -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    <div class="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Investment</p>
          <p class="text-2xl font-bold text-gray-900">AED {{ dashboardData.totalInvestment | aedNumber }}</p>
          <p class="text-xs text-gray-500 mt-1">Since {{ dashboardData.joinDate | date:'medium' }}</p>
        </div>
        <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <i-lucide name="wallet" class="h-6 w-6 text-blue-600"></i-lucide>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Current Value</p>
          <p class="text-2xl font-bold text-gray-900">AED {{ dashboardData.currentValue | aedNumber }}</p>
          <p class="text-xs text-green-600 mt-1 flex items-center" *ngIf="performanceMetrics && performanceMetrics.length">
            <i-lucide name="arrow-up-right" class="h-3 w-3 mr-1"></i-lucide>
            {{ performanceMetrics[0].value }} ROI
          </p>
        </div>
        <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <i-lucide name="trending-up" class="h-6 w-6 text-green-600"></i-lucide>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow-sm p-6 border-l-4 border-amber-500">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Total Profit</p>
          <p class="text-2xl font-bold" [ngClass]="{
              'text-green-600': dashboardData.totalProfit >= 0,
              'text-red-600': dashboardData.totalProfit < 0
            }">
            AED {{ dashboardData.totalProfit >= 0 ? '' : '-' }}{{ (dashboardData.totalProfit >= 0 ? dashboardData.totalProfit : -dashboardData.totalProfit) | aedNumber }}
          </p>
          <p class="text-xs text-gray-500 mt-1" *ngIf="performanceMetrics && performanceMetrics.length">{{ performanceMetrics[0].value }} ROI</p>
        </div>
        <div class="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
          <i-lucide name="award" class="h-6 w-6 text-amber-600"></i-lucide>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-gray-600">Active Portfolios</p>
          <p class="text-2xl font-bold text-gray-900">{{ dashboardData.portfolios.length }}</p>
          <p class="text-xs text-gray-500 mt-1" *ngIf="performanceMetrics && performanceMetrics.length">{{ performanceMetrics[2].value }}</p>
        </div>
        <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
          <i-lucide name="chart-pie" class="h-6 w-6 text-purple-600"></i-lucide>
        </div>
      </div>
    </div>
  </div>

  <!-- Performance Metrics -->
  <div class="bg-white rounded-xl shadow-sm p-6">
    <h3 class="text-lg font-semibold text-gray-900 mb-6">Performance Metrics</h3>
    <div *ngIf="hasPerformanceMetrics; else metricsEmpty" class="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div *ngFor="let metric of performanceMetrics" class="text-center">
        <div class="flex items-center justify-center mb-2">
          <i-lucide [name]="getMetricIcon(metric.trend)" class="h-5 w-5" [ngClass]="getMetricTrendClass(metric.trend)"></i-lucide>
        </div>
        <div class="text-2xl font-bold text-gray-900 mb-1">{{ metric.value }}</div>
        <div class="text-sm text-gray-600 mb-1">{{ metric.label }}</div>
      </div>
    </div>
    <ng-template #metricsEmpty>
      <div class="flex items-center justify-center p-6 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
        <i-lucide name="info" class="h-5 w-5 text-gray-400 mr-2"></i-lucide>
        <p class="text-sm text-gray-600">No performance metrics available yet. Your metrics will appear once your investments have activity.</p>
      </div>
    </ng-template>
  </div>

  <!-- Portfolio Performance and Alerts -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
    <div class="bg-white rounded-xl shadow-sm p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-6">Active Portfolios</h3>
      <div *ngIf="hasPortfolios; else portfoliosEmpty" class="space-y-4">
        <div *ngFor="let portfolio of dashboardData.portfolios" class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <i-lucide [name]="getPortfolioIcon(portfolio.name)" class="h-5 w-5 text-amber-600"></i-lucide>
            </div>
            <div>
              <p class="font-medium text-gray-900">{{ portfolio.name }}</p>
              <p class="text-sm text-gray-600">AED {{ portfolio.investment | aedNumber }} invested</p>
            </div>
          </div>
          <div class="text-right">
            <p class="font-semibold text-gray-900">AED {{ portfolio.currentValue | aedNumber }}</p>
            <div class="flex items-center space-x-2">
              <p class="text-sm text-green-600">+{{ portfolio.roi }}%</p>
              <p class="text-xs text-gray-500">AED +{{ (portfolio?.profit || 0) | aedNumber }}</p>
            </div>
          </div>
        </div>
      </div>
      <ng-template #portfoliosEmpty>
        <div class="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
          <i-lucide name="info" class="h-5 w-5 text-gray-400 inline-block mr-2"></i-lucide>
          <span class="text-sm text-gray-600">No active portfolios found. Start investing to see your portfolios here.</span>
        </div>
      </ng-template>
    </div>

    <div class="bg-white rounded-xl shadow-sm p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-6">Account Alerts</h3>
      <div *ngIf="hasAlerts; else alertsEmpty" class="space-y-4">
        <div *ngFor="let alert of accountAlerts" class="p-4 rounded-lg border-l-4" [ngClass]="getAlertClass(alert.type)">
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0">
              <i-lucide [name]="getAlertIcon(alert.type)" class="h-5 w-5" [ngClass]="getAlertIconClass(alert.type)"></i-lucide>
            </div>
            <div class="flex-1">
              <p class="font-medium text-gray-900">{{ alert.title }}</p>
              <p class="text-sm text-gray-600 mt-1">{{ alert.message }}</p>
              <p class="text-xs text-gray-500 mt-2">{{ alert.time }}</p>
            </div>
          </div>
        </div>
      </div>
      <ng-template #alertsEmpty>
        <div class="p-6 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
          <i-lucide name="info" class="h-5 w-5 text-gray-400 inline-block mr-2"></i-lucide>
          <span class="text-sm text-gray-600">No alerts at the moment.
             <!-- You will be notified here when there is any account activity. -->
            </span>
        </div>
      </ng-template>
    </div>
  </div>

  <!-- Upcoming Payments -->
  <div class="bg-white rounded-xl shadow-sm p-6" *ngIf="false">
    <h3 class="text-lg font-semibold text-gray-900 mb-6">Upcoming Payments</h3>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div *ngFor="let payment of upcomingPayments" class="p-4 bg-green-50 rounded-lg border border-green-200">
        <div class="flex items-center justify-between mb-2">
          <span class="px-2 py-1 text-xs font-medium rounded-full" [ngClass]="getPaymentStatusClass(payment.status)">
            {{ payment.status }}
          </span>
          <i-lucide name="calendar" class="h-4 w-4 text-gray-500"></i-lucide>
        </div>
        <p class="font-medium text-gray-900">{{ payment.portfolio }}</p>
        <p class="text-sm text-gray-600 mb-2">{{ payment.type }}</p>
        <div class="flex justify-between items-center">
          <p class="font-semibold text-green-600">+{{ payment.amount | aedNumber }} AED</p>
          <p class="text-sm text-gray-600">{{ payment.date }}</p>
        </div>
      </div>
    </div>
  </div>
</div>